import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { AngularSvgIconModule } from 'angular-svg-icon';
import { ToastrService } from 'ngx-toastr';

import {
  Client,
  CommandStatus,
  PageInfo,
  PermissionsEnum,
  ScheduleLogsDataIn,
  ScheduleLogsDataOut,
  SortingType,
} from '../../../../api/api-reference';
import { AuthService } from '../../../../services/auth.service';
import { DatepickerComponent } from '../../../core/components/datepicker/datepicker.component';
import { SearchComponent } from '../../../core/components/search/search.component';
import { SortComponent } from '../../../core/components/sort/sort.component';
import { DateRangeInput } from '../../../core/models/date-range-input';
import { PaginationComponent } from '../../../core/components/pagination/pagination.component';
import { FileService } from '../../../../services/file.service';
import { processDateRange } from '../../../../helper/date-utils';
import { generateFileNameAppendix } from '../../../../helper/download-filename';
@Component({
  selector: 'app-scheduler-monitoring',
  standalone: true,
  imports: [
    CommonModule,
    TranslateModule,
    AngularSvgIconModule,
    SearchComponent,
    PaginationComponent,
    SortComponent,
    RouterLink,
    DatepickerComponent,
  ],
  templateUrl: './scheduler-monitoring.component.html',
  styleUrl: './scheduler-monitoring.component.scss',
})
export class SchedulerMonitoringComponent implements OnInit {
  public pageSize: number = 10;
  public pageNumber: number = 1;
  public currentPage: number = 1;
  public numberOfReminders: number = 0;
  public lastPageSize: number = 1;
  activeRow: number | null = null;
  isDatepickerOpen = false;
  PermissionsEnum = PermissionsEnum;
  CommandStatus =CommandStatus;
  data: ScheduleLogsDataOut[] | undefined = [];

  requestData = new ScheduleLogsDataIn({
    pageInfo: new PageInfo({ pageSize: this.pageSize, page: this.pageNumber }),
    search: undefined,
    startDate: undefined,
    endDate: undefined,
    sorting: SortingType.Descending,
    schedulerId: '',
  });

  constructor(
    private toastr: ToastrService,
    public router: Router,
    public route: ActivatedRoute,
    private client: Client,
    public authService: AuthService,
    private fileService: FileService,
  ) { }

  ngOnInit(): void {
    this.requestData.schedulerId = history.state?.monitoringId;
    if(this.requestData.schedulerId)
      this.reloadTable(1);
    else
      this.router.navigate(['/settings/scheduler/schedules']);
  }

  downloadReport(id: string) {
    const fileNameApendix = generateFileNameAppendix();

    this.client.downloadScheduleLog(id).subscribe({
      next: (response) => {
        this.fileService.downloadFile(
          response.data,
          response.fileName || `scheduler-logs_${fileNameApendix}`,
        );
        this.activeRow = null;
      },
      error: (err) => {
        this.toastr.error(err);
      }
    })
  }

  setActiveRow(rowIndex: number | null) {
    this.activeRow = this.activeRow === rowIndex ? null : rowIndex;
  }

  selectedDate: string | null = '';
  onDateRangeChanged(input: DateRangeInput): void {
    const result = processDateRange(input);

    this.requestData.startDate = result.startDate;
    this.requestData.endDate = result.endDate;
    this.selectedDate = result.selectedDate;
    this.filterDatesInTable();
  }

  clearDates(): void {
    this.requestData.startDate = undefined;
    this.requestData.endDate = undefined;
    this.reloadTable();
  }

  handleRowClick(schedulerLogId: string | undefined) {
    this.router.navigate(['/settings/scheduler/commands'], {
      state: { schedulerId: this.requestData.schedulerId, schedulerLogId: schedulerLogId }
    });
  }

  filterDatesInTable() {
    this.client.queryScheduleLogs(this.requestData).subscribe({
      next: (response) => {
        this.data = response.data;
        this.numberOfReminders = response.count || 0;
        this.lastPageSize = Math.ceil(this.numberOfReminders / this.pageSize);
      },
      error: (err) => {
        this.toastr.error(err);
      },
    });
  }

  toggleModal() {
    this.isDatepickerOpen = !this.isDatepickerOpen;
  }

  reloadTable(page?: number) {
    this.pageNumber = page || 0;
    if (page) this.requestData!.pageInfo!.page = this.pageNumber;

    this.client.queryScheduleLogs(this.requestData).subscribe({
      next: (response) => {
        this.data = response.data;
        this.numberOfReminders = response.count || 0;
        this.lastPageSize = Math.ceil(this.numberOfReminders / this.pageSize);
      },
      error: (err) => {
        this.toastr.error(err);
      },
    });
  }

  pageChange(value: number) {
    this.currentPage = value;
    this.reloadTable(this.currentPage);
  }

  onSortOption(selectedSort?: SortingType) {
    if (selectedSort) this.requestData.sorting = selectedSort;
    this.reloadTable();
  }

  searchChange(searchText: string) {
    this.requestData.search = searchText;
    this.reloadTable(1);
  }
}
